import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

function readLegacyJwtRole(value: string): string | null {
  const payload = value.split(".")[1];
  if (!payload) return null;

  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const parsed = JSON.parse(atob(padded)) as { role?: unknown };
    return typeof parsed.role === "string" ? parsed.role : null;
  } catch {
    return null;
  }
}

export function isBrowserSafeSupabaseKey(value: string): boolean {
  return (
    value.startsWith("sb_publishable_") || readLegacyJwtRole(value) === "anon"
  );
}

export const isSupabaseConfigured = Boolean(
  url && publishableKey && isBrowserSafeSupabaseKey(publishableKey),
);

export function createSupabaseFetch(supabaseKey: string): typeof fetch {
  if (!isBrowserSafeSupabaseKey(supabaseKey)) {
    throw new Error(
      "VITE_SUPABASE_PUBLISHABLE_KEY must contain only a public Supabase publishable/anon key.",
    );
  }

  return (input, init) => {
    const headers = new Headers(
      typeof Request !== "undefined" && input instanceof Request
        ? input.headers
        : undefined,
    );

    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) =>
        headers.set(key, value),
      );
    }

    // Lovable Cloud uses Supabase's opaque publishable keys. They belong in
    // `apikey`, not in a Bearer header; user access tokens remain Bearer JWTs.
    if (
      supabaseKey.startsWith("sb_publishable_") &&
      headers.get("Authorization") === `Bearer ${supabaseKey}`
    ) {
      headers.delete("Authorization");
    }
    headers.set("apikey", supabaseKey);

    return fetch(input, { ...init, headers });
  };
}

export const supabase =
  url && publishableKey && isBrowserSafeSupabaseKey(publishableKey)
    ? createClient(url, publishableKey, {
        global: {
          fetch: createSupabaseFetch(publishableKey),
        },
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      })
    : null;
